import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const testimonials = [
  {
    text: "OptiRev improved our revenue by 40% in just 3 months. Their integration of billing and IT is a game-changer.",
    author: "Dr. Sarah Jenkins",
    title: "Clinic Director",
    initial: "S"
  },
  {
    text: "The transition to their integrated RCM and EHR system was seamless. Our claim approval rate is up by 60%.",
    author: "James Miller",
    title: "Practice Administrator",
    initial: "J"
  },
  {
    text: "Finally, a partner that understands both HIPAA security and revenue growth. Their 24/7 support is world-class.",
    author: "Linda Chen",
    title: "Operations Manager",
    initial: "L"
  }
];

const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0); // 1 for right, -1 for left

  // Auto-slide logic
  useEffect(() => {
    const timer = setInterval(() => {
      nextStep();
    }, 5000);
    return () => clearInterval(timer);
  }, [index]);

  const nextStep = () => {
    setDirection(1);
    setIndex((prev) => (prev + 1 === testimonials.length ? 0 : prev + 1));
  };

  const prevStep = () => {
    setDirection(-1);
    setIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  // Animation Variants
  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 200 : -200,
      opacity: 0,
      scale: 0.9
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 200 : -200,
      opacity: 0,
      scale: 0.9
    })
  };

  return (
    <section className="py-24 bg-slate-50 overflow-hidden px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="inline-block bg-[#14b8a6]/10 p-3 rounded-2xl mb-4 text-[#14b8a6]"
          >
            <Quote size={32} />
          </motion.div>
          <h2 className="text-4xl font-bold text-[#1e3a8a]">Trusted by Providers</h2>
          <p className="text-slate-500 mt-4">Real results from practices using the OptiRev model.</p>
        </div>

        <div className="relative min-h-[400px] flex items-center justify-center">
          {/* Navigation Arrows */}
          <button 
            onClick={prevStep}
            className="absolute left-0 z-20 p-3 rounded-full bg-white shadow-lg text-[#1e3a8a] hover:bg-[#14b8a6] hover:text-white transition-all hidden md:block"
          >
            <ChevronLeft size={24} />
          </button>
          
          <button 
            onClick={nextStep}
            className="absolute right-0 z-20 p-3 rounded-full bg-white shadow-lg text-[#1e3a8a] hover:bg-[#14b8a6] hover:text-white transition-all hidden md:block"
          >
            <ChevronRight size={24} />
          </button>

          {/* Sliding Content */}
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={index}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
              className="w-full max-w-2xl bg-white p-12 rounded-[3rem] shadow-xl border border-slate-100 flex flex-col items-center text-center cursor-grab active:cursor-grabbing"
            >
              <div className="flex gap-1 mb-8">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className="fill-[#14b8a6] text-[#14b8a6]" />
                ))}
              </div>

              <p className="text-xl md:text-2xl text-slate-600 leading-relaxed italic mb-10">
                "{testimonials[index].text}"
              </p>

              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-[#1e3a8a] text-white rounded-full flex items-center justify-center text-xl font-black shadow-lg">
                  {testimonials[index].initial}
                </div>
                <div>
                  <p className="text-lg font-bold text-[#1e3a8a]">{testimonials[index].author}</p>
                  <p className="text-sm text-[#14b8a6] font-bold uppercase tracking-widest">{testimonials[index].title}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-3 mt-12">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setDirection(i > index ? 1 : -1);
                setIndex(i);
              }}
              className={`h-2 transition-all duration-300 rounded-full ${
                i === index ? 'w-8 bg-[#14b8a6]' : 'w-2 bg-slate-300'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;